import { z } from 'zod';

const AllProtocols = ['https', 'http'] as const;
type Protocol = (typeof AllProtocols)[number];

const protocolPrefix = (protocol: Protocol) => `${protocol}://`;

export const stripProtocol = (value: string, onStripped: (protocol: Protocol) => void) => {
  for (const protocol of AllProtocols.toSorted((a, b) => b.length - a.length)) {
    if (value.startsWith(protocolPrefix(protocol))) {
      onStripped(protocol);
      return value.slice(protocolPrefix(protocol).length);
    }
  }
  return value;
};

export const UrlSchema = z.string().url();

/**
 * A schema that represents a domain string that may have been provided with an unexpected protocol
 * prefix.  If a protocol prefix is included, it will be stripped and a warning will be logged, but
 * the schema will return the domain without the protocol prefix.
 */
export const DomainSchema = z.string().transform((value, ctx) => {
  const stripped = stripProtocol(value, protocol => {
    /* eslint-disable-next-line no-console -- The logger depends on the environment module so
       the environment module must avoid logger usage. */
    console.warn(
      `The domain was provided with a protocol prefix (${protocol}), which is not ` +
        'expected. The protocol prefix will be stripped and ignored, but please update ' +
        'the environment variable to exclude it.',
      { path: ctx.path, value },
    );
  });
  if (stripped.trim().length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'The domain cannot be empty.',
    });
    return z.NEVER;
  }
  return stripped.trim();
});
